import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  sendOutcomeWording,
  type RecapAudience,
  type RecapDraft,
  type RecapSendResult,
  type Severity,
} from "./meetingRecap.types";

/** The slice of the RAW Feathers service a send calls. */
interface RawService {
  create: (data: object, params?: object) => Promise<unknown>;
}

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;
const codeOf = (err: unknown): number | null => {
  const c = (err as { code?: unknown } | null)?.code;
  return typeof c === "number" ? c : null;
};

/**
 * Meeting Detail → Recap (#3182): draft, review and send a meeting's recap,
 * internal or to the client.
 *
 * THE DRAFT is read through the `meeting-recaps` ServiceStore
 * (meetingIntelligence.stores.ts), keyed by the meeting's id; this store keeps
 * only the FORM — the subject, the text and the recipients as the person
 * edits them, and their "I have reviewed it" — seeded from each draft the
 * server composes. Who may receive it, whether it may be sent at all and why
 * not, all come from the server (`recipients`, `blocked`).
 *
 * THE SEND goes through the RAW Feathers client, as the review's responses do:
 * the raw client rejects every refusal, so a 400 or 403 can never read as a
 * success. What the answer means is worded by `sendOutcomeWording`, which
 * never says "delivered".
 *
 * Loading is a person's act (choosing Internal or Client), not a mount effect;
 * the route only tells this store which meeting is open.
 */
class MeetingRecapStore {
  rootStore: RootStore;
  meetingId: number | null = null;
  /** Null until the person chooses who the recap is for. */
  audience: RecapAudience | null = null;
  loading = false;
  error: string | null = null;
  forbidden = false;

  // ---- the form ----
  subject = "";
  text = "";
  selected: string[] = [];
  reviewed = false;
  /**
   * Other clients the recap was flagged for naming — by the draft, or by the
   * server refusing a send. Several clients are named with ordinary words
   * ("Test", "Premier"), so the sender may confirm a flag is not about them;
   * the server re-checks every send against exactly these names.
   */
  flaggedClients: string[] = [];
  othersConfirmed = false;
  /** Which draft the form was seeded from (`meetingId:audience`); null before any. */
  formFor: string | null = null;

  sending = false;
  actionError: string | null = null;
  outcome: { severity: Severity; text: string } | null = null;

  private loadSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- services ------------------------------------------------------------

  private get service(): ServiceStoreType | null {
    const rs = this.rootStore as unknown as {
      serviceStoreManager?: { getStore?: (name: string) => unknown };
      meetingRecapsStore?: unknown;
    };
    return (
      ((rs.serviceStoreManager?.getStore?.("meeting-recaps") ??
        rs.meetingRecapsStore) as ServiceStoreType | null | undefined) ?? null
    );
  }

  private get raw(): RawService | null {
    const app = (
      this.rootStore as unknown as {
        app?: { service: (p: string) => RawService };
      }
    ).app;
    return app?.service("meeting-recaps") ?? null;
  }

  // ---- derived -------------------------------------------------------------

  /**
   * The draft for this meeting and audience. Each audience is its own cache
   * entry (`<meetingId>:<audience>`), so a slow answer for one can never
   * replace the other's (Codex review).
   */
  get draft(): RecapDraft | null {
    if (this.meetingId == null || !this.audience) return null;
    const key = `${this.meetingId}:${this.audience}`;
    const rows = (this.service?.entities ?? []) as unknown as RecapDraft[];
    return rows.find((r) => r.id === key) ?? null;
  }

  /** Why Send is not available right now, in words; null when it is. */
  get sendBlocker(): string | null {
    const d = this.draft;
    if (!d) return "The recap has not been drafted yet.";
    if (d.blocked) return d.blocked;
    if (!d.email.configured)
      return "Email is not set up on this HQ yet: an admin needs to add the SendGrid key.";
    if (!this.selected.length) return "Choose at least one recipient.";
    if (!this.subject.trim()) return "The recap needs a subject.";
    if (!this.text.trim()) return "The recap has no text.";
    if (d.audience === "client" && !this.reviewed)
      return "Confirm you have read the recap before it goes to the client.";
    return null;
  }

  get canSend(): boolean {
    return !this.sending && this.sendBlocker === null;
  }

  isSelected(email: string): boolean {
    return this.selected.includes(email);
  }

  // ---- loading -------------------------------------------------------------

  setMeetingId(id: number | string | null) {
    const n = Number(id);
    const next = Number.isInteger(n) && n > 0 ? n : null;
    if (next === this.meetingId) return;
    this.dispose();
    this.meetingId = next;
  }

  /** The person chose who the recap is for: draft it. */
  async open(audience: RecapAudience) {
    if (this.audience !== audience) {
      this.audience = audience;
      this.outcome = null;
      this.actionError = null;
    }
    await this.load();
  }

  /**
   * Read the draft. `keepForm` keeps what the person has written (a refused
   * send must not wipe their edits); otherwise the form is seeded afresh.
   */
  async load(options: { keepForm?: boolean } = {}) {
    const ended = sessionMark(this.rootStore);
    const seq = ++this.loadSeq;
    const id = this.meetingId;
    const audience = this.audience;
    const service = this.service;
    if (id == null || !audience) return;
    this.loading = true;
    this.error = null;
    if (!service) {
      this.loading = false;
      this.error = "The meeting-recaps service is not available in this build.";
      return;
    }
    try {
      await service.get(id, {
        query: { audience },
        throwOnError: true,
      } as never);
      if (ended() || seq !== this.loadSeq) return;
      runInAction(() => {
        this.forbidden = false;
        const key = `${id}:${audience}`;
        const draft = this.draft;
        if (draft && (!options.keepForm || this.formFor !== key)) {
          this.subject = draft.subject;
          this.text = draft.text;
          this.selected = draft.recipients
            .filter((r) => r.suggested)
            .map((r) => r.email);
          this.reviewed = false;
          this.flaggedClients = [...draft.otherClients];
          this.othersConfirmed = false;
          this.formFor = key;
        }
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      if (ended() || seq !== this.loadSeq) return;
      runInAction(() => {
        this.forbidden = codeOf(err) === 403;
        this.error = messageOf(err, "Failed to draft this meeting's recap");
      });
    } finally {
      if (!ended() && seq === this.loadSeq)
        runInAction(() => (this.loading = false));
    }
  }

  // ---- the form ------------------------------------------------------------

  setSubject(v: string) {
    this.subject = v;
    this.reviewed = false;
    this.othersConfirmed = false;
  }

  setText(v: string) {
    this.text = v;
    // A changed text is a text nobody has reviewed yet, and a confirmation
    // about the old one says nothing about the new.
    this.reviewed = false;
    this.othersConfirmed = false;
  }

  setOthersConfirmed(v: boolean) {
    this.othersConfirmed = v;
  }

  setReviewed(v: boolean) {
    this.reviewed = v;
  }

  toggleRecipient(email: string) {
    this.selected = this.isSelected(email)
      ? this.selected.filter((e) => e !== email)
      : [...this.selected, email];
  }

  // ---- sending -------------------------------------------------------------

  async send() {
    const ended = sessionMark(this.rootStore);
    const id = this.meetingId;
    const audience = this.audience;
    const raw = this.raw;
    if (id == null || !audience || !raw || !this.canSend) return;
    this.sending = true;
    this.actionError = null;
    this.outcome = null;
    let accepted = false;
    try {
      const result = (await raw.create({
        meetingId: id,
        audience,
        to: [...this.selected],
        subject: this.subject,
        text: this.text,
        ...(audience === "client"
          ? {
              reviewed: this.reviewed,
              acknowledgedOtherClients: this.othersConfirmed
                ? [...this.flaggedClients]
                : [],
            }
          : {}),
      })) as RecapSendResult;
      if (ended()) return;
      accepted = Boolean(result?.accepted);
      runInAction(() => {
        this.outcome = sendOutcomeWording(result);
      });
    } catch (err) {
      if (isSessionEnded(err) || ended()) return;
      const data = (err as { data?: { reason?: unknown; otherClients?: unknown } } | null)?.data;
      runInAction(() => {
        this.actionError = messageOf(err, "HQ could not send this recap.");
        // The server names what it found; the sender removes it or confirms it.
        if (data?.reason === "other-client" && Array.isArray(data.otherClients)) {
          const named = data.otherClients.filter((n): n is string => typeof n === "string");
          this.flaggedClients = [...new Set([...this.flaggedClients, ...named])];
          this.othersConfirmed = false;
        }
      });
    } finally {
      if (!ended()) runInAction(() => (this.sending = false));
    }
    if (ended()) return;
    // The draft carries the "sent from this meeting" list: re-read it. A send
    // SendGrid took starts the next one fresh; anything else keeps the edits.
    await this.load({ keepForm: !accepted });
  }

  /** Sign-out (RootStore.purgeUserData): every field back to its constructed value. */
  reset() {
    this.dispose();
    this.loadSeq = 0;
  }

  dispose() {
    this.meetingId = null;
    this.audience = null;
    this.loading = false;
    this.error = null;
    this.forbidden = false;
    this.subject = "";
    this.text = "";
    this.selected = [];
    this.reviewed = false;
    this.flaggedClients = [];
    this.othersConfirmed = false;
    this.formFor = null;
    this.sending = false;
    this.actionError = null;
    this.outcome = null;
  }
}

export default MeetingRecapStore;
